import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { type QueryObserverResult } from "@tanstack/react-query";
import $api from "@/frontend/shared/api/axios";
import { NormalButton } from "@/frontend/shared/ui/Button";

import styles from "./ImpersonationThing.module.scss";

type Position = {
  x: number;
  y: number;
};

const initialPosition: Position = { x: 16, y: window?.innerHeight - 200 || 200 };

const ImpersonationSessionWidget = ({
  email,
  refetch
}: {
  email?: string;
  refetch?: () => Promise<QueryObserverResult<boolean, Error>>;
}) => {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const initialMousePosition = useRef<Position>({ x: 0, y: 0 });

  const dragRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent): void => {
      if (!isDragging) return;

      const deltaX = e.clientX - initialMousePosition.current.x;
      const deltaY = e.clientY - initialMousePosition.current.y;

      setPosition((prevPosition) => ({
        x: prevPosition.x + deltaX,
        y: prevPosition.y + deltaY
      }));

      initialMousePosition.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = (): void => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    // Prevent dragging when clicking close button
    if (e.target instanceof Element && e.target.closest(`.${styles.closeButton}`)) return;

    setIsDragging(true);
    initialMousePosition.current = { x: e.clientX, y: e.clientY };
  };

  const killSession = async () => {
    await $api.delete("/admin/auth/impersonations");
    await refetch?.();
  };

  return createPortal(
    <div
      className={styles.portal}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`
      }}
      ref={dragRef}
    >
      <div className={styles.header} onMouseDown={handleMouseDown}>
        <h3 className={styles.title}>Impersonation Session</h3>
      </div>
      <div className={styles.content}>
        {email ? <p>Currently impersonating {email}</p> : null}
        <NormalButton size="medium" onClick={killSession}>
          Stop Impersonating
        </NormalButton>
      </div>
    </div>,
    document.body
  );
};

export { ImpersonationSessionWidget };
